import { api, type KnowledgeItemData, type KnowledgeItemStatus } from "./api";
import {
  startKnowledgeBackgroundTracking,
  stopKnowledgeBackgroundTracking,
} from "./knowledgeBackground";

type KnowledgePdfUploadOptions = {
  userId?: string | null;
  onItemChange?: (item: KnowledgeItemData) => void;
  uploadFetch?: typeof fetch;
};

export function buildLocalKnowledgePdfItem(params: {
  id: string;
  filename: string;
  status: KnowledgeItemStatus;
  sizeBytes?: number;
}): KnowledgeItemData {
  const title = params.filename.replace(/\.pdf$/i, "") || params.filename || "PDF資料";
  return {
    id: params.id,
    type: "pdf",
    title,
    status: params.status,
    visibility: "private",
    original_filename: params.filename,
    mime_type: "application/pdf",
    size_bytes: params.sizeBytes ?? null,
    page_count: null,
    text_extractable: null,
    ai_processing_allowed: true,
    failure_reason: null,
    failure_code: null,
    failure_stage: null,
    failure_message: null,
    can_retry: false,
    summary: null,
    has_summary: false,
    summarized_at: null,
    created_at: null,
    updated_at: null,
  };
}

export async function uploadKnowledgePdf(
  file: File,
  options: KnowledgePdfUploadOptions = {}
): Promise<KnowledgeItemData> {
  const uploadFetch = options.uploadFetch ?? fetch;
  let knowledgeItemId = "";
  try {
    const intent = await api.createKnowledgePdfUploadIntent({
      filename: file.name,
      mimeType: file.type || "application/pdf",
      sizeBytes: file.size,
    });
    knowledgeItemId = intent.knowledgeItemId;
    const uploadingItem = buildLocalKnowledgePdfItem({
      id: knowledgeItemId,
      filename: file.name,
      status: "uploading",
      sizeBytes: file.size,
    });
    options.onItemChange?.(uploadingItem);

    const safeUploadHeaders = Object.fromEntries(
      Object.entries(intent.uploadHeaders ?? {}).filter(([key]) => key.toLowerCase() !== "authorization")
    );
    const putResponse = await uploadFetch(intent.uploadUrl, {
      method: "PUT",
      headers: {
        ...safeUploadHeaders,
        "Content-Type": "application/pdf",
      },
      body: file,
    });
    if (!putResponse.ok) {
      throw new Error("PDFのアップロードに失敗しました。通信状況を確認してもう一度お試しください。");
    }

    const completed = await api.completeKnowledgePdfUpload(knowledgeItemId);
    const completedItem = buildLocalKnowledgePdfItem({
      id: completed.id || knowledgeItemId,
      filename: file.name,
      status: completed.status,
      sizeBytes: file.size,
    });
    options.onItemChange?.(completedItem);
    startKnowledgeBackgroundTracking(options.userId, completedItem);
    return completedItem;
  } catch (error) {
    if (knowledgeItemId) stopKnowledgeBackgroundTracking(options.userId, knowledgeItemId);
    throw error;
  }
}
